import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { GitHubBranchProtectionModeDTO } from '@deepguide-ai/types'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { AddRepositoryDialog } from '../scan/AddRepositoryDialog'
import type { RepositoryItem } from '../scan/ConnectionScanCard'
import { GitHubBranchProtectionDialog } from '../scan/GitHubBranchProtectionDialog'

export function useGitHubBranchProtectionSetup(
  workspaceId?: string,
  allowConfiguration = true
) {
  const queryClient = useQueryClient()
  const permissions = useWorkspacePermissions(workspaceId)
  const canWorkspaceWrite = permissions.canWorkspaceWrite && allowConfiguration
  const scope = useRef(workspaceId)
  scope.current = workspaceId
  const active = useRef<string | null>(null)
  const saving = useRef(false)
  const [isSaving, setIsSaving] = useState(false)
  const [session, setSession] = useState<{
    workspaceId: string
    repositories: RepositoryItem[]
    index: number
  } | null>(null)
  const [selectWorkspaceId, setSelectWorkspaceId] = useState<string | null>(
    null
  )
  useEffect(() => {
    active.current = null
    setSession(null)
    setSelectWorkspaceId(null)
  }, [workspaceId])
  const options = trpc.githubApp.listConnectedRepositories.queryOptions({
    workspaceId: workspaceId ?? '',
  })
  const mutation = useMutation(
    trpc.githubApp.updateBranchProtectionMode.mutationOptions()
  )
  const installations = useQuery({
    ...trpc.githubApp.listInstallations.queryOptions(),
    enabled: !!workspaceId && selectWorkspaceId === workspaceId,
  })
  const connect = useMutation(
    trpc.githubApp.connectRepository.mutationOptions()
  )
  const current = session?.workspaceId === workspaceId ? session : null
  const start = async () => {
    if (
      !workspaceId ||
      scope.current !== workspaceId ||
      !canWorkspaceWrite ||
      active.current === workspaceId
    )
      return
    const requestedWorkspaceId = workspaceId
    active.current = requestedWorkspaceId
    try {
      const connected = await queryClient.fetchQuery({
        ...options,
        staleTime: 0,
      })
      if (scope.current !== requestedWorkspaceId) return
      if (!connected.length) {
        setSelectWorkspaceId(requestedWorkspaceId)
        active.current = null
        return
      }
      const unconfigured = connected.filter(
        (repo) => !repo.branchProtectionMode
      )
      if (!unconfigured.length) {
        active.current = null
        return
      }
      setSession({
        workspaceId: requestedWorkspaceId,
        repositories: unconfigured,
        index: 0,
      })
    } catch (error) {
      if (scope.current !== requestedWorkspaceId) return
      active.current = null
      toast.error('Failed to load branch protection settings', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const save = async (
    repository: RepositoryItem,
    mode: GitHubBranchProtectionModeDTO
  ) => {
    if (!workspaceId || scope.current !== workspaceId || !canWorkspaceWrite)
      return false
    if (saving.current) return false
    saving.current = true
    setIsSaving(true)
    const requestedWorkspaceId = workspaceId
    try {
      await mutation.mutateAsync({ repositoryId: repository.id, mode })
      await queryClient.invalidateQueries({
        queryKey: trpc.githubApp.listConnectedRepositories.queryKey({
          workspaceId: requestedWorkspaceId,
        }),
      })
      if (scope.current !== requestedWorkspaceId) return false
      toast.success('Branch protection check updated', {
        description: `${repository.fullName} will use ${mode === 'CLASSIC' ? 'Classic branch protection' : 'GitHub Rulesets'}. Rescan to update existing results.`,
      })
      return true
    } catch (error) {
      if (scope.current !== requestedWorkspaceId) return false
      toast.error('Failed to update branch protection check', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
      return false
    } finally {
      saving.current = false
      setIsSaving(false)
    }
  }

  const connectRepository = async (repositoryId: string) => {
    if (!workspaceId || scope.current !== workspaceId || !canWorkspaceWrite)
      return false
    const requestedWorkspaceId = workspaceId
    try {
      await connect.mutateAsync({ repositoryId, workspaceId })
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.integrations.getConnectedProviderKeys.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.githubApp.listConnectedRepositories.queryKey({
            workspaceId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: trpc.githubApp.listInstallations.queryKey(),
        }),
      ])
      if (scope.current !== requestedWorkspaceId) return false
      toast.success('Repository connected')
      return true
    } catch (error) {
      if (scope.current !== requestedWorkspaceId) return false
      toast.error('Failed to connect repository', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
      return false
    }
  }

  const close = () => {
    active.current = null
    setSession(null)
  }

  return {
    start,
    save,
    connectRepository,
    isConnecting: connect.isPending,
    isSaving,
    dialog: (
      <>
        <AddRepositoryDialog
          open={
            !!workspaceId &&
            selectWorkspaceId === workspaceId &&
            canWorkspaceWrite
          }
          onOpenChange={(open) => {
            if (!open) setSelectWorkspaceId(null)
          }}
          providerName='GitHub'
          repos={(installations.data ?? []).flatMap(
            (installation) => installation.repositories ?? []
          )}
          isRepoLoading={installations.isLoading}
          isConnecting={connect.isPending}
          isRefreshing={false}
          onConnect={async (repository) => {
            if (!(await connectRepository(repository.id))) return
            setSelectWorkspaceId(null)
            await start()
          }}
        />
        <GitHubBranchProtectionDialog
          open={!!current && canWorkspaceWrite}
          repository={current?.repositories[current.index] ?? null}
          progress={
            current && current.repositories.length > 1
              ? `${current.index + 1} / ${current.repositories.length}`
              : undefined
          }
          isSaving={isSaving}
          onOpenChange={(open) => {
            if (!open) close()
          }}
          onSave={async (repository, mode) => {
            if (!(await save(repository, mode))) return false
            if (!current) return false
            if (current.index + 1 === current.repositories.length) close()
            else setSession({ ...current, index: current.index + 1 })
            return false
          }}
        />
      </>
    ),
  }
}
